import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Star, ArrowRight, Phone, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

const TESTIMONIALS = [
  { name: "Sarah Mitchell", location: "Sydney, NSW", text: "Sales Corp Australia saved us over $600 on our annual electricity bill. The entire process took less than 10 minutes and the team handled everything. I couldn't believe how simple it was - wish I'd done it sooner!", rating: 5, type: "Residential Energy", savings: "$612" },
  { name: "James Chen", location: "Melbourne, VIC", text: "As a small business owner running three cafes, energy costs were eating into our margins. Sales Corp found us a plan that cut our costs by 30% across all locations. Their business energy team really knows their stuff.", rating: 5, type: "Business Energy", savings: "$3,200" },
  { name: "Emma Thompson", location: "Brisbane, QLD", text: "I didn't know switching internet providers could save me so much. The comparison was quick and easy, and the Sales Corp team even helped me understand the different speed tiers. Now I'm getting faster internet for less money!", rating: 5, type: "Internet & NBN", savings: "$360" },
  { name: "David Williams", location: "Adelaide, SA", text: "Professional, transparent, and genuinely focused on getting the best deal. They saved us thousands across our three business locations and continue to monitor our plans for better offers. Outstanding service.", rating: 5, type: "Business Energy", savings: "$4,800" },
  { name: "Lisa Nguyen", location: "Perth, WA", text: "We were paying way too much for electricity. The team at Sales Corp found us a better plan that saves us over $700 a year. The process was completely free and stress-free.", rating: 5, type: "Electricity", savings: "$720" },
  { name: "Michael Roberts", location: "Canberra, ACT", text: "Had been putting off comparing energy plans for months because I thought it would be complicated. Sales Corp made it incredibly easy - just a few questions and they did all the work. Great Australian company.", rating: 5, type: "Residential Energy", savings: "$480" },
  { name: "Rachel Cooper", location: "Gold Coast, QLD", text: "After installing solar panels, I needed help finding the best feed-in tariff. Sales Corp not only found me a great rate but also helped me optimise my whole energy plan. Highly recommend their solar expertise!", rating: 5, type: "Solar", savings: "$1,100" },
  { name: "Tom Bradley", location: "Hobart, TAS", text: "I run a plumbing business and was spending a fortune on mobile plans for my team. Sales Corp compared business phone plans and saved us nearly $200 a month. That's money straight back into the business.", rating: 5, type: "Business Phone", savings: "$2,280" },
  { name: "Angela Marino", location: "Newcastle, NSW", text: "Moving to a new house was stressful enough without having to worry about setting up electricity. Sales Corp helped me find a great plan straight away. Made the whole move so much easier.", rating: 5, type: "Electricity", savings: "$750" },
  { name: "Greg Patterson", location: "Geelong, VIC", text: "I was sceptical at first - how could a comparison service be completely free? But it genuinely is. No catch, no hidden fees. They found me a cheaper gas plan and I'm saving about $25 a month. Top service.", rating: 5, type: "Gas", savings: "$300" },
  { name: "Priya Sharma", location: "Parramatta, NSW", text: "As a property manager, I deal with utility setups all the time. Sales Corp has been fantastic for comparing business insurance options for our portfolio. Their team is knowledgeable and always responsive.", rating: 4, type: "Business Insurance", savings: "$1,500" },
  { name: "Chris O'Brien", location: "Darwin, NT", text: "Even in Darwin, Sales Corp was able to help us find better energy rates. Their Australia-wide coverage is impressive and the savings are real. Already recommended them to three other families.", rating: 5, type: "Residential Energy", savings: "$420" },
];

const HERO_BG = "https://images.pexels.com/photos/3334038/pexels-photo-3334038.jpeg?auto=compress&cs=tinysrgb&w=1920";

export default function TestimonialsPage() {
  return (
    <div data-testid="testimonials-page">
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0B203B]/90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="max-w-2xl" initial="hidden" animate="visible" variants={fadeUp}>
            <span className="text-sm font-semibold tracking-wider uppercase text-[#FF6600]">Testimonials</span>
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white mt-2 mb-4">
              Trusted by Thousands of Australians
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed">
              Real stories from real customers who have saved money with Sales Corp Australia.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={i}
                className="bg-white border border-[#E2E8F0] rounded-2xl p-6 card-lift flex flex-col"
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i % 3}
                data-testid={`testimonial-card-${i}`}
              >
                <Quote className="w-8 h-8 text-[#FF6600]/20 mb-3" />
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-[#FF6600] text-[#FF6600]" />
                  ))}
                  {t.rating < 5 && Array.from({ length: 5 - t.rating }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-[#E2E8F0]" />
                  ))}
                </div>
                <p className="text-sm text-[#64748B] leading-relaxed mb-4 flex-1">"{t.text}"</p>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="font-heading font-semibold text-[#0B203B] text-sm">{t.name}</p>
                    <p className="text-xs text-[#64748B]">{t.location}</p>
                    <p className="text-xs text-[#64748B]">{t.type}</p>
                  </div>
                  {t.savings && (
                    <div className="bg-[#10B981]/10 rounded-lg px-3 py-1.5">
                      <p className="text-sm font-bold text-[#10B981]">Saved {t.savings}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
          <div className="absolute inset-0 bg-[#0B203B]/90" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">Join Thousands of Happy Customers</h2>
          <p className="text-gray-300 mb-8">Start your free comparison today and see how much you could save.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/compare">
              <Button className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-full px-8 py-3" data-testid="testimonials-cta-compare">
                Compare Now <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="tel:1300796797">
              <Button variant="outline" className="border-2 border-white text-white font-bold rounded-lg px-8 py-3 hover:bg-white hover:text-[#0B203B]" data-testid="testimonials-cta-call">
                <Phone className="w-4 h-4 mr-2" /> 1300 796 797
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
